// src/PatronForm.js
import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import config from "./config";
import DuplicatePatronErrorModal from "./DuplicatePatronErrorModal";
import "./PatronForm.css";
import logger from './utils/logger';

const PatronForm = ({
  patron,
  orgId,
  organizationName,
  organizations = [],
  existingPatrons = [],
  onClose,
  onSuccess,
}) => {
  const { getAccessToken, user } = useAuth();
  const isFullAdmin = user?.roles?.some((r) => r.name === "Full Admin" || r === "Full Admin");
  const [nfcCredentialType, setNfcCredentialType] = useState("40X");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
  });
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [badgePhoto, setBadgePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);

  // Common country codes with flags
  const countries = [
    { code: "+1", name: "US/Canada", flag: "🇺🇸" },
    { code: "+44", name: "UK", flag: "🇬🇧" },
    { code: "+61", name: "Australia", flag: "🇦🇺" },
    { code: "+81", name: "Japan", flag: "🇯🇵" },
    { code: "+86", name: "China", flag: "🇨🇳" },
    { code: "+33", name: "France", flag: "🇫🇷" },
    { code: "+49", name: "Germany", flag: "🇩🇪" },
    { code: "+39", name: "Italy", flag: "🇮🇹" },
    { code: "+34", name: "Spain", flag: "🇪🇸" },
    { code: "+91", name: "India", flag: "🇮🇳" },
    { code: "+52", name: "Mexico", flag: "🇲🇽" },
    { code: "+55", name: "Brazil", flag: "🇧🇷" },
    { code: "+7", name: "Russia", flag: "🇷🇺" },
    { code: "+82", name: "South Korea", flag: "🇰🇷" },
    { code: "+971", name: "UAE", flag: "🇦🇪" },
  ];

  useEffect(() => {
    if (patron) {
      setFormData({
        firstName: patron.firstName,
        lastName: patron.lastName,
        email: patron.email,
        mobileNumber: patron.mobileNumber,
      });

      // Parse existing phone number
      if (patron.mobileNumber) {
        const phone = patron.mobileNumber;
        if (phone.startsWith("+")) {
          // Find matching country code
          const matchingCountry = countries.find((c) =>
            phone.startsWith(c.code)
          );
          if (matchingCountry) {
            setCountryCode(matchingCountry.code);
            setPhoneDigits(phone.substring(matchingCountry.code.length));
          } else {
            // Default to first match or +1
            setCountryCode("+1");
            setPhoneDigits(phone.substring(1));
          }
        } else {
          // Assume US number without +
          setCountryCode("+1");
          setPhoneDigits(phone);
        }
      }

      // Add cache-busting parameter to prevent browser from showing cached image
      setPhotoPreview(
        `${patron.badgePhotoUrl}?t=${patron.updatedAt || patron.createdAt}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patron]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, "");

    // Check if it starts with +
    if (cleaned.startsWith("+")) {
      const digits = cleaned.substring(1);
      return digits.length >= 7 && digits.length <= 15;
    } else {
      // US format - 10 digits or 11 with leading 1
      return (
        cleaned.length === 10 ||
        (cleaned.length === 11 && cleaned.startsWith("1"))
      );
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = name === "email" ? value.toLowerCase() : value;
    setFormData((prev) => ({ ...prev, [name]: normalizedValue }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    const digits = value.replace(/\D/g, "");
    setPhoneDigits(digits);

    // Update the full phone number in E.164 format
    if (digits) {
      setFormData((prev) => ({ ...prev, mobileNumber: countryCode + digits }));
    } else {
      setFormData((prev) => ({ ...prev, mobileNumber: "" }));
    }

    // Clear error when user starts typing
    if (errors.mobileNumber) {
      setErrors((prev) => ({ ...prev, mobileNumber: null }));
    }
  };

  const handleCountryChange = (e) => {
    const newCode = e.target.value;
    setCountryCode(newCode);

    // Update the full phone number with new country code
    if (phoneDigits) {
      setFormData((prev) => ({ ...prev, mobileNumber: newCode + phoneDigits }));
    }
  };

  const formatPhoneDisplay = (digits) => {
    if (!digits) return "";

    // Format based on country code
    if (countryCode === "+1" && digits.length === 10) {
      // US/Canada format: (555) 555-5555
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    // Default: add spaces every 3-4 digits
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10)
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(
      6,
      10
    )} ${digits.slice(10)}`;
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        badgePhoto:
          "Invalid file type. Please upload a JPG, PNG, or GIF image.",
      }));
      return;
    }

    // Validate file size (2MB)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        badgePhoto: "File too large. Maximum size is 2MB.",
      }));
      return;
    }

    setBadgePhoto(file);
    setErrors((prev) => ({ ...prev, badgePhoto: null }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    } else if (!patron) {
      // Only check for duplicates when creating a new patron (not editing)
      const isDuplicate = existingPatrons.some(
        (p) => p.email.toLowerCase() === formData.email.trim().toLowerCase()
      );
      if (isDuplicate) {
        newErrors.email =
          "A patron with this email already exists in this organization";
      }
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!validatePhone(formData.mobileNumber)) {
      newErrors.mobileNumber =
        "Please enter a valid phone number (US: 10 digits, International: +country code + 7-15 digits)";
    }

    if (!patron && !badgePhoto) {
      newErrors.badgePhoto = "Badge photo is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("orgId", orgId);
      formDataToSend.append("firstName", formData.firstName.trim());
      formDataToSend.append("lastName", formData.lastName.trim());
      formDataToSend.append("email", formData.email.trim());
      formDataToSend.append("mobileNumber", formData.mobileNumber.trim());

      if (badgePhoto) {
        formDataToSend.append("badgePhoto", badgePhoto);
      }

      // Only send nfcCredentialType on new patron creation, not edits
      if (!patron) {
        formDataToSend.append("nfcCredentialType", nfcCredentialType);
      }

      const url = patron
        ? `${config.apiEndpoint}/patrons/${orgId}/${patron.patronId}`
        : `${config.apiEndpoint}/patrons`;

      const method = patron ? "PUT" : "POST";

      const accessToken = await getAccessToken();
      const response = await fetch(url, {
        method,
        headers: {
          "x-api-key": config.apiKey,
          Authorization: `Bearer ${accessToken}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Check if this is a duplicate patron error (409 Conflict)
        if (response.status === 409 && errorData.duplicate) {
          // Show the duplicate error modal
          setDuplicateError({
            ...errorData,
            email: formData.email.trim(),
          });
          return; // Don't throw, let modal handle it
        }

        throw new Error(
          errorData.error || `Failed to ${patron ? "update" : "create"} patron`
        );
      }

      onSuccess();
    } catch (err) {
      logger.error("Error submitting form:", err);
      setErrors({ submit: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="patron-form-overlay" onClick={onClose}>
      <div className="patron-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{patron ? "Edit Patron" : "Create New Patron"}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="patron-form">
          {patron && patron.patronNumber && (
            <div className="form-group patron-number-display">
              <label>Patron Number</label>
              <div className="patron-number-value">{patron.patronNumber}</div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={errors.firstName ? "error" : ""}
              />
              {errors.firstName && (
                <span className="error-text">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                Last Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={errors.lastName ? "error" : ""}
              />
              {errors.lastName && (
                <span className="error-text">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
              <span className="lowercase-badge">lowercase only</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? "error" : ""}
              placeholder="john.doe@example.com"
              disabled={!!patron}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
            {patron && (
              <span className="help-text">
                💡 Email address cannot be changed after patron creation
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="mobileNumber">
              Mobile Number <span className="required">*</span>
            </label>
            <div className="phone-input-container">
              <select
                className="country-code-select"
                value={countryCode}
                onChange={handleCountryChange}
                disabled={submitting}
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                id="mobileNumber"
                className={`phone-number-input ${
                  errors.mobileNumber ? "error" : ""
                }`}
                value={formatPhoneDisplay(phoneDigits)}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
                disabled={submitting}
              />
            </div>
            {errors.mobileNumber && (
              <span className="error-text">{errors.mobileNumber}</span>
            )}
            <span className="help-text">
              💡 Enter digits only (will be formatted as{" "}
              {countryCode + phoneDigits || countryCode + "##########"})
            </span>
          </div>

          {!patron && isFullAdmin && (
            <div className="form-group">
              <label>Credential Type</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="nfcCredentialType"
                    value="40X"
                    checked={nfcCredentialType === "40X"}
                    onChange={() => setNfcCredentialType("40X")}
                    disabled={submitting}
                  />
                  <span>40X <span className="radio-description">(Standard)</span></span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="nfcCredentialType"
                    value="56W"
                    checked={nfcCredentialType === "56W"}
                    onChange={() => setNfcCredentialType("56W")}
                    disabled={submitting}
                  />
                  <span>56W <span className="radio-description">(Extended)</span></span>
                </label>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="badgePhoto">
              Badge Photo {!patron && <span className="required">*</span>}
            </label>
            <div className="photo-upload-container">
              {photoPreview && (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Badge preview" />
                </div>
              )}
              <div className="photo-upload-controls">
                <input
                  type="file"
                  id="badgePhoto"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handlePhotoChange}
                  className="file-input"
                />
                <label htmlFor="badgePhoto" className="file-input-label">
                  📷 {photoPreview ? "Change Photo" : "Upload Photo"}
                </label>
                <span className="help-text">
                  JPG, PNG, or GIF. Max 2MB. Will be resized to 640x640.
                </span>
              </div>
            </div>
            {errors.badgePhoto && (
              <span className="error-text">{errors.badgePhoto}</span>
            )}
          </div>

          {errors.submit && <div className="form-error">{errors.submit}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-small"></span>
                  {patron ? "Updating..." : "Creating..."}
                </>
              ) : patron ? (
                "Update Patron"
              ) : (
                "Create Patron"
              )}
            </button>
          </div>
        </form>
      </div>

      {duplicateError && (
        <DuplicatePatronErrorModal
          errorData={duplicateError}
          organizationName={organizationName}
          organizations={organizations}
          onBack={() => {
            // Clear the email field and close the duplicate error modal
            setFormData({ ...formData, email: "" });
            setDuplicateError(null);
            // Form stays open with pre-populated data except email
          }}
        />
      )}
    </div>
  );
};

export default PatronForm;
